import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, ChevronRight, ChevronLeft, Check, Monitor, Puzzle, FolderOpen } from 'lucide-react';

// Resolve the absolute URL against the backend so the anchor navigates through
// the public ingress (Cloudflare allows direct-navigation downloads but may
// challenge fetch/XHR). This guarantees the browser treats it as a real
// user-initiated navigation that receives `Content-Disposition: attachment`.
function resolveAbsoluteUrl(path) {
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  if (/^https?:\/\//i.test(path)) return path;
  if (backend) return backend.replace(/\/$/, '') + path;
  return path;
}

// Bulletproof download trigger:
//  1. Synchronous anchor-click appended to document.body (outside the Radix
//     Dialog portal -> no event interception). Preserves the user-gesture so
//     browsers don't flag it as a programmatic download.
//  2. Backend sends `Content-Disposition: attachment` so the browser downloads
//     rather than navigates. No Blob / fetch / CORS complexity.
//  3. Fallbacks: hidden iframe, then window.open — covers iOS Safari, strict
//     privacy modes, and ad-blocker edge cases.
function triggerDownload(url, filename) {
  const absolute = resolveAbsoluteUrl(url);
  const cacheBusted = absolute + (absolute.includes('?') ? '&' : '?') + 't=' + Date.now();

  // Primary: direct anchor click (most reliable for Content-Disposition responses)
  try {
    const a = document.createElement('a');
    a.href = cacheBusted;
    a.download = filename;
    a.rel = 'noopener';
    a.style.position = 'fixed';
    a.style.left = '-9999px';
    a.style.top = '-9999px';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { a.remove(); } catch (_) {} }, 1500);
  } catch (_) {
    // Fallback 1: hidden iframe
    try {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;';
      iframe.src = cacheBusted;
      document.body.appendChild(iframe);
      setTimeout(() => { try { iframe.remove(); } catch (_) {} }, 15000);
    } catch (_) {
      // Fallback 2: open in a new tab — browser downloads via attachment header
      window.open(cacheBusted, '_blank', 'noopener,noreferrer');
    }
  }
}

function DownloadButton({ url, filename, label, testId }) {
  const [clicked, setClicked] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        triggerDownload(url, filename);
        setClicked(true);
        setTimeout(() => setClicked(false), 2500);
      }}
      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-slate-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-slate-700 transition-all shadow-lg cursor-pointer select-none"
      data-testid={testId}
    >
      <Download className="w-5 h-5" />
      {clicked ? 'Download started — click again to re-download' : label}
    </button>
  );
}

const CHROMIUM_STEPS = [
  {
    title: 'Download the Extension',
    icon: Download,
    content: (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">Click the button below to download the Plexura AI Scribe extension for Chrome, Edge, or Brave.</p>
        <DownloadButton url="/api/extension/download/chrome" filename="plexura-ai-scribe-extension.zip" label="Download Extension (.zip)" testId="download-extension-zip" />
        <p className="text-xs text-gray-500">Then unzip the downloaded file to a folder on your computer.</p>
      </div>
    )
  },
  {
    title: 'Open Extensions Page',
    icon: Monitor,
    content: (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">Open your browser's extensions page:</p>
        <div className="space-y-2">
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
            <span className="text-sm font-semibold text-blue-800 w-16">Chrome</span>
            <code className="text-xs bg-white px-3 py-1.5 rounded-lg border text-blue-700 select-all">chrome://extensions</code>
          </div>
          <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl p-3">
            <span className="text-sm font-semibold text-indigo-800 w-16">Edge</span>
            <code className="text-xs bg-white px-3 py-1.5 rounded-lg border text-indigo-700 select-all">edge://extensions</code>
          </div>
          <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl p-3">
            <span className="text-sm font-semibold text-orange-800 w-16">Brave</span>
            <code className="text-xs bg-white px-3 py-1.5 rounded-lg border text-orange-700 select-all">brave://extensions</code>
          </div>
        </div>
      </div>
    )
  },
  {
    title: 'Enable Developer Mode',
    icon: Puzzle,
    content: (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">On the extensions page, enable <strong>Developer mode</strong>:</p>
        <div className="bg-gray-50 rounded-2xl p-5 border">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-700">Developer mode</span>
            <div className="w-12 h-6 bg-purple-600 rounded-full relative">
              <div className="absolute right-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow" />
            </div>
          </div>
          <p className="text-xs text-gray-500">Toggle in the top-right corner of the extensions page.</p>
        </div>
      </div>
    )
  },
  {
    title: 'Load & Use',
    icon: FolderOpen,
    content: (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">Click <strong>"Load unpacked"</strong> and select the unzipped folder:</p>
        <div className="bg-gray-50 rounded-2xl p-5 border">
          <div className="flex items-center gap-2">
            <div className="bg-white border rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm">Load unpacked</div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-xs text-gray-600">Select the unzipped folder</span>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-green-800 flex items-center gap-2"><Check className="w-4 h-4" />Extension ready!</p>
          <p className="text-xs text-green-700 mt-1">Log into Plexura AI Scribe, then click the extension icon — credentials auto-populate.</p>
        </div>
      </div>
    )
  }
];

const SAFARI_STEPS = [
  {
    title: 'Download Safari Extension',
    icon: Download,
    content: (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">Download the Safari-compatible extension source for macOS.</p>
        <DownloadButton url="/api/extension/download/safari" filename="plexura-safari-extension.zip" label="Download Safari Extension (.zip)" testId="download-safari-zip" />
        <p className="text-xs text-gray-500">Requires macOS Ventura+ and Xcode 15+ (free from the App Store).</p>
      </div>
    )
  },
  {
    title: 'Convert with Xcode',
    icon: Monitor,
    content: (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">Unzip the download, then open <strong>Terminal</strong> and run:</p>
        <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
          <code className="text-xs text-green-400 select-all whitespace-pre">{`xcrun safari-web-extension-converter \\
  /path/to/unzipped-folder \\
  --bundle-identifier com.plexura.aiscribe \\
  --app-name "Plexura AI Scribe"`}</code>
        </div>
        <p className="text-xs text-gray-500">This generates an Xcode project. Open it with <strong>Product &gt; Run</strong> (Cmd+R).</p>
      </div>
    )
  },
  {
    title: 'Enable in Safari',
    icon: Puzzle,
    content: (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">After building in Xcode, enable the extension in Safari:</p>
        <div className="space-y-2">
          <div className="flex items-center gap-3 bg-gray-50 border rounded-xl p-3">
            <span className="text-xs font-semibold text-gray-700 w-4">1.</span>
            <span className="text-sm text-gray-700">Safari &gt; Settings &gt; Extensions</span>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border rounded-xl p-3">
            <span className="text-xs font-semibold text-gray-700 w-4">2.</span>
            <span className="text-sm text-gray-700">Toggle <strong>ON</strong> "Plexura AI Scribe"</span>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border rounded-xl p-3">
            <span className="text-xs font-semibold text-gray-700 w-4">3.</span>
            <span className="text-sm text-gray-700">Grant permission for <strong>"All Websites"</strong></span>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-xs text-amber-700">For development, enable: Safari &gt; Settings &gt; Advanced &gt; <strong>"Show features for web developers"</strong>, then Safari &gt; Develop &gt; <strong>"Allow Unsigned Extensions"</strong>.</p>
        </div>
      </div>
    )
  },
  {
    title: 'Ready to Use',
    icon: FolderOpen,
    content: (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-green-800 flex items-center gap-2"><Check className="w-4 h-4" />Safari extension ready!</p>
          <p className="text-xs text-green-700 mt-2">Log into Plexura AI Scribe in Safari, then click the extension icon in the toolbar — credentials auto-populate.</p>
          <p className="text-xs text-green-700 mt-1">All features work the same: EHR auto-detection, section-by-section paste, floating sidebar.</p>
        </div>
        <p className="text-xs text-gray-500">The extension also works on <strong>iOS Safari</strong> (iPhone/iPad) if you build the iOS target in Xcode.</p>
      </div>
    )
  }
];

export default function ExtensionInstallGuide({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [browser, setBrowser] = useState('chromium'); // 'chromium' | 'safari'

  const handleClose = () => {
    setStep(0);
    onOpenChange(false);
  };

  const steps = browser === 'safari' ? SAFARI_STEPS : CHROMIUM_STEPS;
  const currentStep = steps[step];
  const StepIcon = currentStep.icon;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="extension-install-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold bg-gradient-to-r from-purple-600 to-slate-700 bg-clip-text text-transparent">
            Install Universal EHR Extension
          </DialogTitle>
        </DialogHeader>

        {/* Browser selector */}
        <div className="flex gap-2 mt-1" data-testid="browser-selector">
          <button
            onClick={() => { setBrowser('chromium'); setStep(0); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all border ${
              browser === 'chromium' ? 'bg-purple-50 border-purple-300 text-purple-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
            }`}
            data-testid="select-chromium"
          >
            <Monitor className="w-3.5 h-3.5" />
            Chrome / Edge / Brave
          </button>
          <button
            onClick={() => { setBrowser('safari'); setStep(0); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all border ${
              browser === 'safari' ? 'bg-purple-50 border-purple-300 text-purple-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
            }`}
            data-testid="select-safari"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            Safari (macOS / iOS)
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mt-2" data-testid="install-step-indicator">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                i < step ? 'bg-green-100 text-green-700' :
                i === step ? 'bg-purple-600 text-white shadow-lg' :
                'bg-gray-100 text-gray-400'
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 rounded ${i < step ? 'bg-green-300' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-purple-100 p-2.5 rounded-xl">
              <StepIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold">Step {step + 1} of {steps.length}</p>
              <h3 className="font-bold text-gray-900">{currentStep.title}</h3>
            </div>
          </div>
          {currentStep.content}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            variant="outline"
            data-testid="install-prev-step"
            className="rounded-xl"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          {step < steps.length - 1 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              data-testid="install-next-step"
              className="rounded-xl bg-purple-600 hover:bg-purple-700"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleClose}
              data-testid="install-done"
              className="rounded-xl bg-green-600 hover:bg-green-700"
            >
              <Check className="w-4 h-4 mr-1" />
              Done
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
